import { useContext, useEffect, createContext, ReactNode, useState } from 'react';
import moment from 'moment';
import { routes } from '../router/route-config';
import { getLandingPage } from '../router';
import { Spinner } from 'shared/components';
import { User } from '../../features/auth/models';
import { AppRouteType } from 'shared/types';
import { createSearchParams, matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getAccessToken,
  getAuthenticationResult,
  getRefreshToken,
  removeToken,
  setToken,
} from 'features/auth/services/token.service';
import { useAppDispatch } from 'app/store';
import { accountService } from 'features/auth/services';
import { useMergeState } from 'shared/hooks';
import { fetchSaleById } from 'app/store/slices/sale.slice';

interface AuthContextType {
  isFirstLoading: boolean;
  loading: boolean;
  accessToken: null | string | undefined;
  idToken: null | string | undefined;
  profile: null | User;
  isAuth: boolean;
  landingPage?: null | AppRouteType;
  onSignIn: (credentials: { email: string; password: string }) => Promise<void>;
  onSignOut: () => void;
  onRefreshToken: () => void;
  getProfile: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isFirstLoading: true,
  loading: false,
  accessToken: null,
  idToken: null,
  profile: null,
  isAuth: false,
  landingPage: null,
  onSignIn: () => Promise.resolve(),
  onSignOut: () => {},
  onRefreshToken: () => {},
  getProfile: () => {},
});

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [landingPage, setLandingPage] = useState<AppRouteType | null>(null);
  const dispatch = useAppDispatch();

  const [userData, setUserData] = useMergeState({
    isFirstLoading: true,
    loading: false,
    idToken: null as string | null | undefined,
    accessToken: null as string | null | undefined,
    isAuth: false,
    profile: null,
    landingPage: null,
  });

  const currentPage = routes?.find((_: AppRouteType) => (matchPath(_.path, location.pathname) ? true : false));

  const onSignIn = async (credentials: { email: string; password: string }) => {
    try {
      setUserData({ loading: true });
      const { AuthenticationResult } = await accountService.signIn(credentials);
      setSearchParams(searchParams);
      await setToken(AuthenticationResult);
      setUserData({
        accessToken: AuthenticationResult.AccessToken,
        idToken: AuthenticationResult.IdToken,
      });
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw new Error(error as string);
    }
  };

  const onRefreshToken = async () => {
    try {
      const refreshToken = await getRefreshToken();
      const { AuthenticationResult } = await accountService.refreshToken(refreshToken);
      await setToken(AuthenticationResult);
      setUserData({
        accessToken: AuthenticationResult.AccessToken,
        idToken: AuthenticationResult.IdToken,
      });
    } catch (error) {
      setUserData({ isFirstLoading: false, loading: false });
      throw new Error(error as string);
    }
  };

  const getProfile = async () => {
    try {
      setUserData({ loading: true });
      const accessToken = await getAccessToken();
      const user = await accountService.profile({
        accessToken,
      });
      const landingPage = getLandingPage(user?.Role);
      setLandingPage(landingPage);
      setUserData({
        isFirstLoading: false,
        loading: false,
        profile: user,
        isAuth: true,
      });
      if (user?.roleId == 2 && user?.id && user?.Role?.name === 'Sales') {
        dispatch(fetchSaleById(user.id));
      }
      // Don't navigate here - let the useEffect handle navigation after profile is loaded
    } catch (error) {
      console.error(error);
      onSignOut();
    }
  };

  // Load initial tokens on mount
  useEffect(() => {
    const loadInitialTokens = async () => {
      const tokenStorage = await getAuthenticationResult();
      if (tokenStorage?.IdToken || tokenStorage?.AccessToken) {
        setUserData({
          idToken: tokenStorage.IdToken || null,
          accessToken: tokenStorage.AccessToken || null,
        });
      } else {
        setUserData({ isFirstLoading: false });
      }
    };
    loadInitialTokens();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(
      () => {
        if (userData?.idToken) {
          onRefreshToken();
        }
      },
      1000 * 60 * 60 * 10,
    );
    return () => clearInterval(intervalId);
  }, [userData?.idToken]);

  useEffect(() => {
    if (!userData?.idToken) {
      if (currentPage?.auth) {
        setUserData({ isFirstLoading: false });
        // Save the intended destination before redirecting to login
        // But don't redirect if we're already on login page
        if (location.pathname !== '/login') {
          const redirectTo = location.pathname + location.search;
          navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`, { replace: true });
        }
      } else {
        setUserData({ isFirstLoading: false });
      }
      return;
    }

    if (!currentPage) {
      // Only redirect to landing page if we're not already on a valid route
      // Don't redirect if we're on a route that just hasn't been matched yet
      // This prevents redirecting away from valid pages like /renouvellement-de-benne
      if (landingPage?.path && location.pathname !== landingPage.path) {
        navigate(landingPage.path, { replace: true });
      }
      return;
    }

    if (userData?.idToken && !userData.profile) {
      const checkAndRefreshToken = async () => {
        const tokenStorage = await getAuthenticationResult();
        if (
          tokenStorage?.LoginDate &&
          tokenStorage?.LoginDate < moment().add(-10, 'hours').format('YYYY-MM-DD HH:mm:ss')
        ) {
          await onRefreshToken();
        }
        getProfile();
      };
      checkAndRefreshToken();
    }
  }, [userData?.idToken, userData?.profile, currentPage]);

  useEffect(() => {
    if (!userData?.idToken) {
      // Save the intended destination before redirecting to login
      // But don't redirect if we're already on login page (prevents infinite loop)
      if (location.pathname !== '/login') {
        const redirectTo = location.pathname + location.search;
        navigate(`/login?redirect=${encodeURIComponent(redirectTo)}`, { replace: true });
      }
      return;
    }

    if (userData?.idToken && userData?.profile) {
      if (currentPage?.roles) {
        navigate('/404', { replace: true });
        return;
      }

      // Check if there's a redirect parameter in the URL (from login redirect)
      const redirectParam = searchParams.get('redirect');
      if (redirectParam && currentPage?.name === 'Login') {
        const redirectTo = decodeURIComponent(redirectParam);

        // Prevent infinite loop: don't redirect to /login
        if (redirectTo.startsWith('/login')) {
          // If redirect points to login, just remove the redirect param and stay on login
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('redirect');
          const newSearch = newSearchParams.toString();
          navigate(
            {
              pathname: '/login',
              search: newSearch || undefined,
            },
            { replace: true },
          );
          return;
        }

        // Only redirect if we're not already on that page
        if (location.pathname + location.search !== redirectTo) {
          // Parse the redirect URL
          const redirectPath = redirectTo.split('?')[0];
          const redirectSearch = redirectTo.includes('?') ? redirectTo.split('?')[1] : '';

          // Remove redirect param from search params if it exists
          const newSearchParams = new URLSearchParams(redirectSearch);
          newSearchParams.delete('redirect');
          const finalSearch = newSearchParams.toString();

          // Navigate to the redirect destination
          navigate(
            {
              pathname: redirectPath,
              search: finalSearch || undefined,
            },
            { replace: true },
          );
        }
        return;
      }

      // Check if current page is a valid authenticated route
      // If currentPage is found and it's authenticated, don't redirect - stay on current page
      if (currentPage?.auth) {
        // We're on a valid authenticated route, don't redirect
        return;
      }

      // Only redirect to landing page if we're on Login page and have a landing page
      if (currentPage?.name === 'Login' && landingPage?.path) {
        // Only redirect if we're not already there
        if (location.pathname !== landingPage.path) {
          navigate(landingPage.path, { replace: true });
        }
      }
    }
  }, [
    userData?.idToken,
    userData?.profile,
    currentPage,
    landingPage,
    location.pathname,
    location.search,
    navigate,
    searchParams,
  ]);

  const onSignOut = async () => {
    try {
      if (userData?.profile) {
        const accessToken = await getAccessToken();
        await accountService.signOut({ accessToken });
      }
    } catch (error) {
      console.error(error);
    }
    setUserData({
      isFirstLoading: false,
      loading: false,
      accessToken: null,
      idToken: null,
      isAuth: false,
      profile: null,
    });
    removeToken();
    const tokenQuery = searchParams.get('token');
    navigate(
      {
        pathname: '/login',
        search: tokenQuery
          ? createSearchParams({
              token: tokenQuery,
            }).toString()
          : '',
      },
      { replace: true },
    );
  };

  return (
    <AuthContext.Provider
      value={{
        onSignIn,
        onSignOut,
        getProfile,
        landingPage,
        onRefreshToken,
        isFirstLoading: userData.isFirstLoading,
        loading: userData.loading,
        accessToken: userData.accessToken,
        idToken: userData.idToken,
        profile: userData.profile,
        isAuth: userData.isAuth,
      }}
    >
      {userData?.isFirstLoading || currentPage?.roles ? <Spinner /> : children}
    </AuthContext.Provider>
  );
}

const useAuthContext = () => useContext(AuthContext);

export default useAuthContext;
